import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Tooltip } from 'examkrackers-components'

import { QuizDiagnosticsTagColor } from '../../types'
import {
  getQuizDiagnosticsPaletteColor,
  QuizDiagnosticsPaletteVariant
} from '../../utils/getQuizDiagnosticsPaletteColor'
import {
  CHAPTER_VIEW_PERCENT_LINE_HEIGHT,
  CHAPTER_VIEW_PROGRESS_BAR_HEIGHT,
  CHAPTER_VIEW_PROGRESS_BAR_WIDTH,
  CHAPTER_VIEW_SECTION_MARGIN,
  CHAPTER_VIEW_SUBTITLE_LINE_HEIGHT
} from '../constants'
import { useSrqStatus } from '../../utils/srqStatusStore'

const SUBTITLE_CLAMP_LINES = 2

type ChapterViewTruncatedSubtitleProps = {
  barTitle: string
  subtitle: string
}

const ChapterViewTruncatedSubtitle = ({
  barTitle,
  subtitle
}: ChapterViewTruncatedSubtitleProps): JSX.Element => {
  const subtitleRef = useRef<HTMLDivElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  const updateTruncation = useCallback(() => {
    const element = subtitleRef.current

    if (!element) {
      return
    }

    setIsTruncated(element.scrollHeight > element.clientHeight + 1)
  }, [])

  useEffect(() => {
    updateTruncation()

    const element = subtitleRef.current

    if (!element) {
      return undefined
    }

    const resizeObserver = new ResizeObserver(updateTruncation)
    resizeObserver.observe(element)

    return () => {
      resizeObserver.disconnect()
    }
  }, [subtitle, updateTruncation])

  const subtitleContent = <Subtitle ref={subtitleRef}>{subtitle}</Subtitle>

  if (!isTruncated) {
    return subtitleContent
  }

  return (
    <Tooltip
      tooltipContent={
        <SubtitleTooltipContent>{subtitle}</SubtitleTooltipContent>
      }
      id={`chapter-view-subtitle-${barTitle.replace(/\s+/g, '-').toLowerCase()}`}
      place='top'
    >
      {subtitleContent}
    </Tooltip>
  )
}

type ChapterViewProgressBarProps = {
  barAriaLabel?: string
  color: QuizDiagnosticsTagColor
  contentWidth: number
  fillVariant: QuizDiagnosticsPaletteVariant
  hasQuizDueToday?: boolean
  chapterSrqRedDotsVisible?: boolean
  sectionSrqRedDotsVisible?: boolean
  onBarClick?: () => void
  onTitleClick?: () => void
  percent: number
  showTitleUnderline?: boolean
  subtitle?: string | null
  title: string
  titleAriaLabel?: string
  barWidth?: number
}

const ChapterViewProgressBar = ({
  barAriaLabel,
  color,
  contentWidth,
  fillVariant,
  hasQuizDueToday = false,
  chapterSrqRedDotsVisible = true,
  sectionSrqRedDotsVisible = true,
  onBarClick,
  onTitleClick,
  percent,
  showTitleUnderline = false,
  subtitle,
  title,
  titleAriaLabel,
  barWidth = CHAPTER_VIEW_PROGRESS_BAR_WIDTH
}: ChapterViewProgressBarProps): JSX.Element => {
  const { t } = useTranslation()
  const { srqRedDotsVisible } = useSrqStatus()
  const showDueTodayDot =
    srqRedDotsVisible &&
    chapterSrqRedDotsVisible &&
    sectionSrqRedDotsVisible &&
    hasQuizDueToday
  const isBarClickable = Boolean(onBarClick)
  const isTitleClickable = Boolean(showTitleUnderline && onTitleClick)
  const tooltipIdBase = title.replace(/\s+/g, '-').toLowerCase()

  const handleBarClick = () => {
    if (!onBarClick) {
      return
    }

    onBarClick()
  }

  const handleDueTodayDotClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isBarClickable) {
      return
    }

    event.stopPropagation()
    handleBarClick()
  }

  const handleDueTodayDotKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (!isBarClickable) {
      return
    }

    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    handleBarClick()
  }

  const handleBarKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    handleBarClick()
  }

  const handleTitleClick = () => {
    if (!onTitleClick) {
      return
    }

    onTitleClick()
  }

  const handleTitleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    handleTitleClick()
  }

  const progressBarFill = (
    <ProgressBarFillMask>
      <ProgressBarFill
        $color={color}
        $fillVariant={fillVariant}
        $percent={percent}
      />
    </ProgressBarFillMask>
  )

  const barButton = (
    <ProgressBarShell $barWidth={barWidth}>
      <ProgressBarButton
        type='button'
        aria-label={barAriaLabel ?? title}
        onClick={handleBarClick}
        onKeyDown={handleBarKeyDown}
        $color={color}
      >
        {progressBarFill}
      </ProgressBarButton>
      {showDueTodayDot ? (
        <DueTodayDot
          aria-hidden
          $isClickable={isBarClickable}
          onClick={handleDueTodayDotClick}
          onKeyDown={handleDueTodayDotKeyDown}
          role={isBarClickable ? 'button' : undefined}
          tabIndex={isBarClickable ? 0 : undefined}
        />
      ) : null}
    </ProgressBarShell>
  )

  const titleButton = (
    <TitleButton
      type='button'
      aria-label={titleAriaLabel ?? title}
      onClick={handleTitleClick}
      onKeyDown={handleTitleKeyDown}
    >
      {title}
    </TitleButton>
  )

  return (
    <Container>
      <ContentColumn $contentWidth={contentWidth}>
        <Percent>{`${percent}%`}</Percent>
        {isBarClickable ? (
          <Tooltip
            tooltipContent={t('quizDiagnostics.sectionBar.startNewQuiz')}
            id={`chapter-view-bar-${tooltipIdBase}`}
            place='top'
          >
            {barButton}
          </Tooltip>
        ) : (
          <ProgressBarShell $barWidth={barWidth}>
            <ProgressBarContainer $color={color}>
              {progressBarFill}
            </ProgressBarContainer>
            {showDueTodayDot ? (
              <DueTodayDot aria-hidden $isClickable={false} />
            ) : null}
          </ProgressBarShell>
        )}
        <TitleContainer $contentWidth={contentWidth}>
          {isTitleClickable ? (
            <Tooltip
              tooltipContent={t('quizDiagnostics.sectionBar.readSection')}
              id={`chapter-view-title-${tooltipIdBase}`}
              place='top'
            >
              {titleButton}
            </Tooltip>
          ) : (
            <Title $showUnderline={showTitleUnderline}>{title}</Title>
          )}
        </TitleContainer>
        {subtitle ? (
          <ChapterViewTruncatedSubtitle barTitle={title} subtitle={subtitle} />
        ) : null}
      </ContentColumn>
    </Container>
  )
}

export default ChapterViewProgressBar

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  flex: 1;
  position: relative;
`

const ContentColumn = styled.div<{ $contentWidth: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: ${({ $contentWidth }) => $contentWidth}px;
  flex: 1;
  margin-top: 8px;
`

const Percent = styled.div`
  font-family: Arial, sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: ${CHAPTER_VIEW_PERCENT_LINE_HEIGHT}px;
  color: #444444;
  margin-bottom: ${CHAPTER_VIEW_SECTION_MARGIN}px;
`

const ProgressBarShell = styled.div<{ $barWidth: number }>`
  position: relative;
  width: ${({ $barWidth }) => $barWidth}px;
  height: ${CHAPTER_VIEW_PROGRESS_BAR_HEIGHT}px;
  margin-bottom: ${CHAPTER_VIEW_SECTION_MARGIN}px;
  overflow: visible;
`

const ProgressBarContainer = styled.div<{
  $color: QuizDiagnosticsTagColor
}>`
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.main.white};
  border: 0.68px solid
    ${({ theme, $color }) =>
      getQuizDiagnosticsPaletteColor($color, 'stroke') ??
      theme.colors.tags[$color]?.backgroundActive};
  border-radius: 4px;
  position: relative;
  overflow: hidden;
  box-shadow: 0px 1px 4px 0px #00000040;
`

const ProgressBarButton = styled.button<{
  $color: QuizDiagnosticsTagColor
}>`
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  background-color: ${({ theme }) => theme.colors.main.white};
  border: 0.68px solid
    ${({ theme, $color }) =>
      getQuizDiagnosticsPaletteColor($color, 'stroke') ??
      theme.colors.tags[$color]?.backgroundActive};
  border-radius: 4px;
  position: relative;
  overflow: hidden;
  box-shadow: 0px 1px 4px 0px #00000040;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #f7961b;
    outline-offset: 2px;
  }
`

const ProgressBarFill = styled.div<{
  $color: QuizDiagnosticsTagColor
  $fillVariant: QuizDiagnosticsPaletteVariant
  $percent: number
}>`
  width: 100%;
  position: absolute;
  bottom: 0;
  left: 0;
  background: ${({ theme, $color, $fillVariant }) =>
    getQuizDiagnosticsPaletteColor($color, $fillVariant) ??
    theme.colors.tags[$color]?.backgroundActive};
  border-radius: 2px 2px 0 0;
  transition: height 0.3s ease-in-out;
  height: ${({ $percent }) => `calc(${$percent} * 1%)`};
`

const ProgressBarFillMask = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 0;
  position: relative;
  overflow: hidden;
`

const TitleContainer = styled.div<{ $contentWidth: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: ${({ $contentWidth }) => $contentWidth}px;
  text-align: center;
`

const Title = styled.div<{ $showUnderline: boolean }>`
  font-family: Arial, sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 14px;
  min-width: 0;
  width: 100%;
  margin-bottom: 2px;
  text-decoration: ${({ $showUnderline }) =>
    $showUnderline ? 'underline' : 'none'};
  color: #444444;
`

const TitleButton = styled.button`
  font-family: Arial, sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 14px;
  min-width: 0;
  width: 100%;
  margin: 0 0 2px;
  padding: 0;
  border: none;
  background: transparent;
  text-decoration: underline;
  color: #444444;
  cursor: pointer;
  text-align: center;

  &:focus-visible {
    outline: 2px solid #f7961b;
    outline-offset: 2px;
  }
`

const Subtitle = styled.div`
  font-size: 10px;
  font-weight: 400;
  min-width: 0;
  max-width: 100%;
  width: 100%;
  padding: 0 8px;
  box-sizing: border-box;
  line-height: ${CHAPTER_VIEW_SUBTITLE_LINE_HEIGHT}px;
  min-height: calc(
    ${CHAPTER_VIEW_SUBTITLE_LINE_HEIGHT}px * ${SUBTITLE_CLAMP_LINES}
  );
  max-height: calc(
    ${CHAPTER_VIEW_SUBTITLE_LINE_HEIGHT}px * ${SUBTITLE_CLAMP_LINES}
  );
  display: -webkit-box;
  -webkit-line-clamp: ${SUBTITLE_CLAMP_LINES};
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  overflow-wrap: anywhere;
  cursor: default;
  text-align: center;
  font-family: Arial, sans-serif;
  color: #444444;
`

const SubtitleTooltipContent = styled.div`
  padding: 5px;
  max-width: 294px;
  font-size: 10px;
  font-weight: 400;
  line-height: ${CHAPTER_VIEW_SUBTITLE_LINE_HEIGHT}px;
  text-align: center;
  white-space: normal;
  word-break: break-word;
`

const DueTodayDot = styled.div<{ $isClickable: boolean }>`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ea4335;
  z-index: 2;
  pointer-events: auto;
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'default')};

  &:focus-visible {
    outline: 2px solid #f7961b;
    outline-offset: 2px;
  }
`
